import { createContext, useContext, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import api from "@/lib/api";

const TaxYearContext = createContext(null);
const STORAGE_KEY = "tx_tax_ay";
const DEFAULT_AY = "2026-27";
const FALLBACK_AYS = [{ ay: DEFAULT_AY, fy: "2025-26", fy_start: "2025-04-01", fy_end: "2026-03-31", computable: true }];

export function TaxYearProvider({ children }) {
  const [ays, setAys] = useState(FALLBACK_AYS);
  const [currentAy, setCurrentAy] = useState(DEFAULT_AY);
  const [ay, setAyState] = useState(() => localStorage.getItem(STORAGE_KEY) || DEFAULT_AY);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tax/reference");
        if (data?.supported_ays?.length) {
          setAys(data.supported_ays);
          setCurrentAy(data.current_ay || DEFAULT_AY);
          const valid = data.supported_ays.some((a) => a.ay === (localStorage.getItem(STORAGE_KEY) || DEFAULT_AY));
          if (!valid) setAy(data.current_ay || DEFAULT_AY);
        }
      } catch {
        /* keep fallback — reference is best-effort */
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setAy = (v) => {
    setAyState(v);
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
  };

  const meta = ays.find((a) => a.ay === ay) || ays[0] || FALLBACK_AYS[0];
  const value = {
    ay,
    setAy,
    ays,
    meta,
    fy: meta?.fy,
    fyStart: meta?.fy_start,
    fyEnd: meta?.fy_end,
    isComputable: !!meta?.computable,
    isCurrent: ay === currentAy,
  };
  return <TaxYearContext.Provider value={value}>{children}</TaxYearContext.Provider>;
}

/** Pathless layout route element — wraps all /tax/* pages in the provider. */
export function TaxYearLayout() {
  return (
    <TaxYearProvider>
      <Outlet />
    </TaxYearProvider>
  );
}

export function useTaxYear() {
  const ctx = useContext(TaxYearContext);
  if (!ctx) {
    return {
      ay: DEFAULT_AY, setAy: () => {}, ays: FALLBACK_AYS, meta: FALLBACK_AYS[0],
      fy: "2025-26", fyStart: "2025-04-01", fyEnd: "2026-03-31", isComputable: true, isCurrent: true,
    };
  }
  return ctx;
}
